// Salary split (Increment B) — how this person's NET pay is divided across
// their APPROVED payment destinations. Payroll owns the split; the employee
// profile owns the destinations.
//
// What this screen has to make unmissable:
//   • No split = 100% to the default destination (the normal case).
//   • Shares are percentages that must add up to EXACTLY 100% — shown live.
//   • A change is a proposal: it does nothing until a different payroll
//     approver (never the employee themselves) approves it. Runs already
//     locked keep the instructions they were locked with.
//   • When the destinations can't be checked, that is said — it never reads
//     as "no destinations".
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, Info, Loader2, Plus, SplitSquareHorizontal, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Badge } from '@/features/shared/components/ui/badge';
import { DataState } from '@/features/shared/components/ui/DataState';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/features/shared/components/ui/select';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  useSalaryAllocation, useProposeSalaryAllocation, useDecideSalaryAllocation,
  type AllocationPlan, type AllocationDestination, type AllocationPlanStatus,
} from '../api/salaryAllocationApi';
import {
  allocationTotals, bpToPercent, destinationsState, percentToBP, MAX_SPLIT_LINES,
} from '../allocationModel';

const labelCls = 'text-xs text-muted-foreground';

const STATUS_VARIANT: Record<AllocationPlanStatus, 'success' | 'warning' | 'danger' | 'secondary'> = {
  approved: 'success',
  pending_approval: 'warning',
  rejected: 'danger',
  cancelled: 'secondary',
  superseded: 'secondary',
};

interface Props {
  employeeId: string;
  /** payroll:write — may propose / withdraw. */
  canEdit: boolean;
  /** payroll:approve — may approve / reject (server enforces SoD). */
  canApprove: boolean;
}

interface Row { methodId: string; percent: string }

const todayISO = () => new Date().toISOString().slice(0, 10);

export const SalaryAllocationCard: React.FC<Props> = ({ employeeId, canEdit, canApprove }) => {
  const { t } = useTranslation('payroll');
  const prompt = usePrompt();
  const query = useSalaryAllocation(employeeId);
  const propose = useProposeSalaryAllocation(employeeId);
  const decide = useDecideSalaryAllocation();

  const [formOpen, setFormOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>([{ methodId: '', percent: '' }, { methodId: '', percent: '' }]);
  const [effectiveFrom, setEffectiveFrom] = useState(todayISO());
  const [note, setNote] = useState('');

  const data = query.data;
  const dState = destinationsState({
    loading: query.isLoading,
    error: query.isError,
    status: data?.destinations_status,
    destinations: data?.destinations,
  });
  const dests = data?.destinations ?? [];
  const byId = useMemo(() => new Map(dests.map((d) => [d.id, d])), [dests]);
  const defaultDest = dests.find((d) => d.is_default);
  const totals = allocationTotals(rows.map((r) => r.percent));
  const rowsComplete = rows.every((r) => r.methodId);
  const duplicate = new Set(rows.map((r) => r.methodId)).size !== rows.length;

  const destLabel = (d?: AllocationDestination, fallback?: string) =>
    d ? `${d.provider ? `${d.provider} ` : ''}${d.masked}` : fallback ?? '—';

  const statusLabel = (s: AllocationPlanStatus) =>
    t(`salarySplit.status.${s}`, {
      defaultValue: {
        approved: 'Approved', pending_approval: 'Awaiting approval', rejected: 'Rejected',
        cancelled: 'Withdrawn', superseded: 'Replaced',
      }[s],
    });

  const resetForm = () => {
    setRows([{ methodId: '', percent: '' }, { methodId: '', percent: '' }]);
    setEffectiveFrom(todayISO());
    setNote('');
    setFormOpen(false);
  };

  const submit = async (backToDefault: boolean) => {
    try {
      await propose.mutateAsync({
        effective_from: effectiveFrom,
        note: note.trim() || undefined,
        lines: backToDefault
          ? []
          : rows.map((r) => ({ payment_method_id: r.methodId, percent_bp: percentToBP(r.percent) ?? 0 })),
      });
      toast.success(t('salarySplit.proposed', { defaultValue: 'Salary split sent for approval.' }));
      resetForm();
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('salarySplit.proposeFailed', { defaultValue: 'Could not propose the salary split.' })));
    }
  };

  const act = async (plan: AllocationPlan, action: 'approve' | 'reject' | 'cancel') => {
    let reason: string | undefined;
    if (action !== 'approve') {
      const r = await prompt({
        title: action === 'reject'
          ? t('salarySplit.rejectTitle', { defaultValue: 'Reject this salary split?' })
          : t('salarySplit.withdrawTitle', { defaultValue: 'Withdraw this salary split?' }),
        label: t('salarySplit.reason', { defaultValue: 'Reason' }),
        required: true,
        multiline: true,
        minLength: 3,
        confirmLabel: action === 'reject'
          ? t('salarySplit.reject', { defaultValue: 'Reject' })
          : t('salarySplit.withdraw', { defaultValue: 'Withdraw' }),
        danger: true,
      });
      if (r === null) return;
      reason = r.trim();
    }
    try {
      await decide.mutateAsync({ id: plan.id, action, reason });
      toast.success(
        action === 'approve'
          ? t('salarySplit.approved', { defaultValue: 'Salary split approved.' })
          : action === 'reject'
            ? t('salarySplit.rejected', { defaultValue: 'Salary split rejected.' })
            : t('salarySplit.withdrawn', { defaultValue: 'Salary split withdrawn.' }),
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('salarySplit.actionFailed', { defaultValue: 'That did not go through.' })));
    }
  };

  const PlanLines: React.FC<{ plan: AllocationPlan }> = ({ plan }) =>
    plan.lines.length === 0 ? (
      <p className="text-sm text-foreground">
        {t('salarySplit.backToDefault', { defaultValue: '100% to the default payment method' })}
      </p>
    ) : (
      <ul className="space-y-1">
        {plan.lines.map((l) => (
          <li key={l.seq} className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate text-foreground">{destLabel(byId.get(l.payment_method_id), l.masked)}</span>
            <span className="tabular-nums font-semibold text-foreground">{bpToPercent(l.percent_bp)}%</span>
          </li>
        ))}
      </ul>
    );

  const totalsMessage = () => {
    switch (totals.state) {
      case 'ok':
        return t('salarySplit.totalOk', { defaultValue: 'Adds up to 100%.' });
      case 'under':
        return t('salarySplit.totalUnder', { defaultValue: '{{left}}% still to assign.', left: bpToPercent(totals.remainingBP) });
      case 'over':
        return t('salarySplit.totalOver', { defaultValue: '{{over}}% too much.', over: bpToPercent(-totals.remainingBP) });
      case 'invalid':
        return t('salarySplit.totalInvalid', { defaultValue: 'Each share must be a number above 0 with at most two decimals.' });
      default:
        return '';
    }
  };

  const header = (
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-sm font-medium">
        <SplitSquareHorizontal className="h-4 w-4 text-muted-foreground" aria-hidden />
        {t('salarySplit.title', { defaultValue: 'Salary split' })}
      </CardTitle>
    </CardHeader>
  );

  if (query.isLoading) {
    return <Card>{header}<CardContent><DataState variant="loading" compact /></CardContent></Card>;
  }
  if (query.isError || !data) {
    return (
      <Card>
        {header}
        <CardContent>
          <DataState
            variant="error"
            compact
            message={getApiErrorMessage(query.error, t('salarySplit.loadFailed', { defaultValue: 'Could not load the salary split.' }))}
            onRetry={() => void query.refetch()}
          />
        </CardContent>
      </Card>
    );
  }

  const effective = data.effective;
  const pending = data.pending;
  const history = data.plans.filter((p) => p.id !== pending?.id);

  return (
    <Card>
      {header}
      <CardContent className="space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {t('salarySplit.explainer', {
              defaultValue:
                'Without a split, all net pay goes to the default payment method. A split divides net pay by percentage across 2–3 approved methods. Every change needs approval from a different payroll approver, and runs already locked are never changed.',
            })}
          </span>
        </div>

        {/* In force today */}
        <section className="space-y-2">
          <p className={labelCls}>{t('salarySplit.inForce', { defaultValue: 'In force today' })}</p>
          {effective ? (
            <div className="rounded-lg border border-border p-3 space-y-2">
              <PlanLines plan={effective} />
              <p className="text-xs text-muted-foreground">
                {t('salarySplit.since', { defaultValue: 'Since {{date}}', date: effective.effective_from.slice(0, 10) })}
              </p>
            </div>
          ) : (
            <p className="text-sm text-foreground">
              {dState === 'ok'
                ? t('salarySplit.noneWithDefault', {
                    defaultValue: '100% to the default payment method ({{label}}).',
                    label: destLabel(defaultDest, t('salarySplit.noDefault', { defaultValue: 'none set yet' })),
                  })
                : t('salarySplit.none', { defaultValue: '100% to the default payment method.' })}
            </p>
          )}
          {data.stale_destination_ids.length > 0 && (
            <div role="alert" className="flex items-start gap-2 rounded-md bg-warning-soft p-3 text-sm text-warning-strong">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {t('salarySplit.stale', {
                defaultValue:
                  'A method in this split is no longer approved. Payroll will not pay this split until a new one is approved — propose a new split.',
              })}
            </div>
          )}
        </section>

        {/* Destinations health — never blank-as-all-clear */}
        {dState === 'unavailable' && (
          <DataState
            variant="unavailable"
            compact
            message={t('salarySplit.destsUnavailable', {
              defaultValue: 'Payment methods could not be checked right now, so a split cannot be proposed. Try again shortly.',
            })}
          />
        )}
        {dState === 'empty' && (
          <DataState
            variant="empty"
            compact
            message={t('salarySplit.destsEmpty', {
              defaultValue: 'This person has no approved payment method yet — approve one before setting up a split.',
            })}
          />
        )}

        {/* Pending proposal */}
        {pending && (
          <section className="space-y-2 rounded-lg border border-warning p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-foreground">
                {t('salarySplit.pendingTitle', { defaultValue: 'Proposed from {{date}}', date: pending.effective_from.slice(0, 10) })}
              </p>
              <Badge variant="warning">{statusLabel(pending.status)}</Badge>
            </div>
            <PlanLines plan={pending} />
            {pending.note && <p className="text-xs text-muted-foreground">{pending.note}</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              {canApprove && (
                <>
                  <Button size="sm" disabled={decide.isPending} onClick={() => void act(pending, 'approve')}>
                    {t('salarySplit.approve', { defaultValue: 'Approve' })}
                  </Button>
                  <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => void act(pending, 'reject')}>
                    {t('salarySplit.reject', { defaultValue: 'Reject' })}
                  </Button>
                </>
              )}
              {canEdit && (
                <Button size="sm" variant="ghost" disabled={decide.isPending} onClick={() => void act(pending, 'cancel')}>
                  {t('salarySplit.withdraw', { defaultValue: 'Withdraw' })}
                </Button>
              )}
            </div>
          </section>
        )}

        {/* Propose */}
        {canEdit && !pending && dState === 'ok' && !formOpen && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setFormOpen(true)} disabled={dests.length < 2}>
              <Plus className="mr-1 h-4 w-4" aria-hidden />
              {effective
                ? t('salarySplit.change', { defaultValue: 'Change split' })
                : t('salarySplit.add', { defaultValue: 'Split salary' })}
            </Button>
            {effective && effective.lines.length > 0 && (
              <Button size="sm" variant="ghost" disabled={propose.isPending} onClick={() => void submit(true)}>
                {t('salarySplit.proposeDefault', { defaultValue: 'Go back to 100% default' })}
              </Button>
            )}
            {dests.length < 2 && (
              <p className="w-full text-xs text-muted-foreground">
                {t('salarySplit.needTwo', { defaultValue: 'A split needs at least two approved payment methods.' })}
              </p>
            )}
          </div>
        )}

        {canEdit && formOpen && dState === 'ok' && (
          <section className="space-y-3 rounded-lg border border-border p-3">
            {rows.map((r, i) => (
              <div key={i} className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <span className={labelCls}>{t('salarySplit.destination', { defaultValue: 'Payment method' })}</span>
                  <Select value={r.methodId} onValueChange={(v) => setRows((p) => p.map((x, j) => (j === i ? { ...x, methodId: v } : x)))}>
                    <SelectTrigger aria-label={t('salarySplit.destination', { defaultValue: 'Payment method' })}>
                      <SelectValue placeholder={t('salarySplit.choose', { defaultValue: 'Choose…' })} />
                    </SelectTrigger>
                    <SelectContent>
                      {dests.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{destLabel(d)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24 shrink-0">
                  <span className={labelCls}>%</span>
                  <Input
                    inputMode="decimal"
                    value={r.percent}
                    aria-label={t('salarySplit.percent', { defaultValue: 'Share (%)' })}
                    onChange={(e) => setRows((p) => p.map((x, j) => (j === i ? { ...x, percent: e.target.value } : x)))}
                  />
                </div>
                {rows.length > 2 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('salarySplit.removeRow', { defaultValue: 'Remove' })}
                    onClick={() => setRows((p) => p.filter((_, j) => j !== i))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {rows.length < MAX_SPLIT_LINES && dests.length > rows.length && (
              <Button size="sm" variant="ghost" onClick={() => setRows((p) => [...p, { methodId: '', percent: '' }])}>
                <Plus className="mr-1 h-4 w-4" aria-hidden />
                {t('salarySplit.addRow', { defaultValue: 'Add another method' })}
              </Button>
            )}

            <p
              aria-live="polite"
              className={`text-sm ${totals.state === 'ok' ? 'text-success-strong' : 'text-warning-strong'}`}
            >
              {t('salarySplit.total', { defaultValue: 'Total: {{sum}}%', sum: bpToPercent(totals.sumBP) })} — {totalsMessage()}
            </p>
            {duplicate && rowsComplete && (
              <p className="text-sm text-danger-strong">
                {t('salarySplit.duplicate', { defaultValue: 'Each payment method can be used only once.' })}
              </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={labelCls}>{t('salarySplit.effectiveFrom', { defaultValue: 'Effective from' })}</span>
                <Input type="date" min={todayISO()} value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
              </label>
              <label className="block">
                <span className={labelCls}>{t('salarySplit.note', { defaultValue: 'Note (optional)' })}</span>
                <Input value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={propose.isPending || totals.state !== 'ok' || !rowsComplete || duplicate || !effectiveFrom}
                onClick={() => void submit(false)}
              >
                {propose.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />}
                {t('salarySplit.submit', { defaultValue: 'Send for approval' })}
              </Button>
              <Button size="sm" variant="outline" disabled={propose.isPending} onClick={resetForm}>
                {t('salarySplit.cancel', { defaultValue: 'Cancel' })}
              </Button>
            </div>
          </section>
        )}

        {/* History */}
        {history.length > 0 && (
          <section className="space-y-2">
            <p className={labelCls}>{t('salarySplit.history', { defaultValue: 'History' })}</p>
            <ul className="divide-y divide-border">
              {history.map((p) => (
                <li key={p.id} className="space-y-1 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      {t('salarySplit.fromDate', { defaultValue: 'From {{date}}', date: p.effective_from.slice(0, 10) })}
                    </span>
                    <Badge variant={STATUS_VARIANT[p.status]}>{statusLabel(p.status)}</Badge>
                  </div>
                  <PlanLines plan={p} />
                  {p.decision_reason && (
                    <p className="text-xs text-muted-foreground">
                      {t('salarySplit.reasonLabel', { defaultValue: 'Reason: {{reason}}', reason: p.decision_reason })}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </CardContent>
    </Card>
  );
};

export default SalaryAllocationCard;
